/**
 * What the Investor File's Accreditation card is allowed to claim.
 *
 * STARSEED-CAPMARKETS-177. The card used to render a status badge and an
 * approve button over nothing at all: the member's intake answers were dropped
 * by a projection two functions before the payload was built, so an operator
 * making a regulatory determination had no evidence on screen and a button
 * that fired a milestone gating qualification and funding.
 *
 * Three cases reach this function, and conflating any two of them is the whole
 * defect:
 *
 *   member_submitted   the member filled the intake form. Real evidence.
 *   operator_recorded  `mark-accredited-approved` synthesized the record when
 *                      the member had none. It is a by-product OF the approval
 *                      — presenting it as evidence FOR the approval would mean
 *                      approving someone manufactures their own justification.
 *   none               nothing on file. The majority case in production, and
 *                      therefore the one the copy has to be honest about.
 *
 * The server classifies (`verification.provenance`) so the panel and any future
 * surface cannot drift apart on the predicate; this function only decides how
 * the classification is presented.
 */

/** The three questions that DECIDE accreditation.
 *
 *  Split out from the profile below in STARSEED-CAPMARKETS-178 (AC9). They
 *  were always first in the list, but "first in a ten-row two-column grid" is
 *  not the same as "at the decision point" — a reviewer scanning for the
 *  member's own claim had to find it among source-of-funds and citizenship.
 *  The card now renders these as their own group, beside the evidence
 *  submitted for them.
 *
 *  This is the most the platform can honestly do while OQ-178-2 (which Reg D
 *  exemption, which jurisdiction) is open: 506(b) and 506(c) differ on whether
 *  self-attestation can suffice at all, so the surface shows the claim and the
 *  evidence and leaves sufficiency to the reviewer rather than encoding a
 *  standard nobody has chosen yet.
 */
export const ACCREDITATION_ELIGIBILITY_CLAIMS = [
  ["is_accredited_investor", "Accredited investor"],
  ["is_qualified_purchaser", "Qualified purchaser"],
  ["is_professional_investor_outside_us", "Professional investor (non-US)"],
];

/** The profile around the claims. */
export const ACCREDITATION_ANSWERS = [
  ["is_us_person", "US person"],
  ["entity_type", "Entity type"],
  ["intended_role", "Intended role"],
  ["intended_allocation_range", "Intended allocation"],
  ["primary_source_of_funds", "Source of funds"],
  ["country_of_residence", "Country of residence"],
  ["citizenship", "Citizenship"],
];

const ACK_LABELS = {
  compliance_review_only: "Understands this is a compliance review only",
  no_approval_guarantee: "Understands approval is not guaranteed",
  data_sharing_consent: "Consents to data sharing with the regulated partner",
  no_investment_advice: "Understands no investment advice is given",
};

/** A leading ISO calendar date. Slicing the first ten characters is not a date
 *  check — `"not-a-date-at-all".slice(0, 10)` is `"not-a-date"`, which this
 *  helper would previously have handed to the card as a submission date while
 *  its own comment claimed it refused junk (found by Copilot on #2431). */
const ISO_DATE_PREFIX = /^(\d{4}-\d{2}-\d{2})/;

/** `2026-08-23T21:04:32.123Z` → `2026-08-23`; anything else → null. Dates are
 *  decoration here, so a malformed one drops out rather than blanking the card
 *  — but a fabricated one is worse than an absent one on a compliance surface. */
export function shortDate(value) {
  if (!value || typeof value !== "string") return null;
  const match = ISO_DATE_PREFIX.exec(value);
  return match ? match[1] : null;
}

/** `business_income` → `Business income`; `yes` → `Yes`. */
export function formatAnswer(value) {
  if (value === true) return "Yes";
  if (value === false) return "No";
  if (value === null || value === undefined || value === "") return null;
  const text = String(value).replace(/_/g, " ").trim();
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** How to present what an approval rested on (STARSEED-CAPMARKETS-178 AC6).
 *
 *  The server classifies (`accreditation_determination_basis.kind`) so this and
 *  any future surface cannot drift apart on the predicate; this only decides
 *  the wording. `unevidenced` is deliberately the loudest of the three — it is
 *  the majority of existing approvals in production, and the whole reason 177
 *  and 178 exist is that it used to look identical to a reviewed one.
 */
const BASIS_COPY = {
  documented: (n) => `Approved on ${n} reviewed document${n === 1 ? "" : "s"}.`,
  attestation_only: () =>
    "Approved on a signed self-certification alone — no third-party document.",
  unevidenced: (n, submitted) => submitted > 0
    ? `Approved with NO approved evidence, though the member submitted ${submitted} item${submitted === 1 ? "" : "s"}.`
    : "Approved with no evidence on file — an operator attestation.",
};

export function determinationBasis(detail) {
  const basis = detail?.accreditation_determination_basis;
  if (!basis || !basis.kind) return null;
  const evidence = Array.isArray(basis.evidence) ? basis.evidence : [];
  const copy = BASIS_COPY[basis.kind];
  return {
    kind: basis.kind,
    isEvidenced: basis.kind === "documented",
    evidence,
    // An unrecognised kind must not render as a blank reassuring row. Saying
    // the kind out loud is worse-looking and more honest than saying nothing.
    text: copy
      ? copy(evidence.length, basis.submitted_count || 0)
      : `Approval basis recorded as "${basis.kind}".`,
  };
}

/**
 * @param {object|null|undefined} detail  the `/compliance/admin/{id}` payload
 * @returns {{kind: string, state: string, isMemberEvidence: boolean,
 *            headline: string, answers: Array, acknowledgments: Array,
 *            trail: Array, submittedAt: string|null}}
 */
export function accreditationAttestation(detail) {
  const record = detail?.verification || null;
  const state = detail?.accreditation_state || "not_started";
  const requestedAt = shortDate(detail?.accreditation_started_at);

  if (!record) {
    return {
      kind: "none",
      state,
      isMemberEvidence: false,
      headline: state === "in_review"
        ? `Review requested${requestedAt ? ` on ${requestedAt}` : ""} — the member has submitted no intake answers. Approving is an operator attestation.`
        : "No accreditation intake on file. Approving is an operator attestation.",
      eligibilityClaims: [],
      answers: [],
      acknowledgments: [],
      trail: [],
      submittedAt: null,
    };
  }

  const trail = Array.isArray(record.audit_trail) ? record.audit_trail : [];

  if (record.provenance !== "member_submitted") {
    const created = shortDate(record.submitted_at);
    return {
      kind: "operator_recorded",
      state,
      isMemberEvidence: false,
      // Deliberately not "submitted on" — nothing was submitted. This record
      // exists because someone pressed approve.
      headline: `No member submission. This record was created by the approval${created ? ` on ${created}` : ""} — it is an operator attestation, not evidence.`,
      eligibilityClaims: [],
      answers: [],
      acknowledgments: [],
      trail,
      submittedAt: null,
    };
  }

  const eligibilityClaims = ACCREDITATION_ELIGIBILITY_CLAIMS
    .map(([key, label]) => ({ label, value: formatAnswer(record[key]) }))
    .filter((c) => c.value !== null);

  const answers = ACCREDITATION_ANSWERS
    .map(([key, label]) => [label, formatAnswer(record[key])])
    .filter(([, value]) => value !== null)
    .map(([label, value]) => ({ label, value }));

  const acknowledgments = Object.entries(record.acknowledgments || {})
    .map(([key, accepted]) => ({
      label: ACK_LABELS[key] || formatAnswer(key),
      accepted: Boolean(accepted),
    }));

  const submittedAt = shortDate(record.submitted_at);
  return {
    kind: "member_submitted",
    state,
    isMemberEvidence: true,
    headline: `Member attestation${submittedAt ? ` submitted ${submittedAt}` : ""} — self-attested, unverified against documents.`,
    eligibilityClaims,
    answers,
    acknowledgments,
    trail,
    submittedAt,
  };
}
